import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
// import { authHeaders } from "../../../utils/auth";
// import { authorizedFetch } from "../../../utils/auth";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
/**
 * Inactivar pines de un cliente.
 * Props:
 *  - cliente: { id, codigo, nombre }
 *  - onClose: () => void
 *  - onSaved: ({ total, seleccionadas, actualizadas, rechazadas }) => void
 */
export default function InactivarPines({ cliente, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [pines, setPines] = useState([]); // [{ id, etiqueta }]
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openPicker, setOpenPicker] = useState(false);

  // ===== Endpoints =====
  const CLIENTES_BASE = (
    apiRoutes?.clientes?.list || "/flpa/api/clientes"
  ).replace(/\/list(?:\?.*)?$/, "");
  const PINS_ACTIVOS_URL = (clienteId) =>
    `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/pins/activos`;
  const PINS_INACTIVAR_URL = (clienteId) =>
    `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/pins/inactivar`;

  // Helper
  const toId = (v) => (v != null ? String(v) : "");

  // Etiqueta: "responsable - 4 dígitos de pin" (si viene responsable/nombre, lo usamos)
  const makeEtiqueta = (pin) => {
    const resp = pin.responsableNombre != null  ? `${pin.responsableNombre}` : "SIN RESPONSABLE";
    const num = pin.numero != null ? "PIN: "+String(pin.numero) : "";
    const padded = num.length < 4 ? num.padStart(4, "0") : num;
    const base = [padded,resp].filter(Boolean).join(" - ");
    return base || toId(pin.id);
  };

  // 👇 Evitar doble GET en desarrollo (StrictMode): saltamos la PRIMERA ejecución
  const devRunRef = useRef(false);

  // ===== Carga inicial (pines activos del cliente) =====
  useEffect(() => {
    let alive = true;

    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true; // primera pasada en dev → no ejecutar aún
        return;
      }
    }

    (async () => {
      if (!cliente?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const url = PINS_ACTIVOS_URL(cliente.id);
        // const resp = await fetch(url, {
        //               headers: authHeaders()
        //             });
        // if (!resp.ok) {
        //   const t = await resp.text();
        //   throw new Error(`GET ${url} -> ${resp.status} ${resp.statusText}. Body: ${t}`);
        // }
        // const data = await resp.json(); // { clienteId, pins: [...] }
        const data = await apiFetchJson(url); // ✅ wrapper (401/403 + JSON)
        if (!alive) return;

        const items = Array.isArray(data?.pins) ? data.pins : [];
        const norm = items
          .map((p) => ({
            id: toId(p.id),
            etiqueta: makeEtiqueta(p),
            raw: p,
          }))
          .filter((p) => p.id);

        setPines(norm);
        setSelectedIds(new Set(norm.map((p) => p.id))); // preselecciona todos
      } catch (e) {
        console.error("[InactivarPines] Error cargando pines:", e);
        alert("No se pudieron cargar los pines activos del cliente.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [cliente?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtro en el dropdown
  const filtrados = useMemo(() => {
    if (!search) return pines;
    const q = search.toLowerCase();
    return pines.filter((p) =>
      `${p.id} ${p.etiqueta}`.toLowerCase().includes(q),
    );
  }, [pines, search]);

  const total = pines.length;
  const seleccionadas = selectedIds.size;
  const canSave = seleccionadas > 0;

  // Selección
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const marcarTodas = () => setSelectedIds(new Set(pines.map((p) => p.id)));
  const desmarcarTodas = () => setSelectedIds(new Set());

  // Guardar (PATCH /clientes/{id}/pins/inactivar)
  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!canSave || !cliente?.id) return;

    setSaving(true);
    try {
      const ids = Array.from(selectedIds);
      // intentar mandarlos como números (el id viene numérico del backend)
      const idsNum = ids
        .map((s) => {
          const n = Number(s);
          return Number.isFinite(n) ? n : null;
        })
        .filter((n) => n != null);

      const url = PINS_INACTIVAR_URL(cliente.id);
      // const resp = await authorizedFetch(url, {
      //   method: "PATCH",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ pinIds: idsNum }),
      // });
      // if (!resp.ok) {
      //   const t = await resp.text();
      //   throw new Error(`PATCH ${url} -> ${resp.status} ${resp.statusText}. Body: ${t}`);
      // }
      // const r = await resp.json(); // { total, actualizadas, rechazadas }
      const r = await appFetchJson(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinIds: idsNum }),
      }); // ✅ wrapper (lanza si !ok)

      onSaved?.({
        total,
        seleccionadas: ids.length,
        actualizadas: r?.actualizadas ?? 0,
        rechazadas: r?.rechazadas ?? [],
      });
      onClose?.();
    } catch (err) {
      console.error("[InactivarPines] Error al inactivar:", err);
      alert("No se pudo inactivar los pines seleccionados.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Inactivar Pines" onClose={onClose}>
      {loading ? (
        <div className="py-8 text-center">Cargando…</div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">
          {/* █ NUEVA FILA: Cliente sobre el que se trabaja */}
          <div className="border rounded bg-gray-50">
            <div className="px-4 py-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="sm:w-40 text-gray-700 font-medium">
                  Cliente:
                </div>
                <div className="flex-1">
                  <div className="py-2 text-gray-800">
                    {cliente ? (
                      <>
                        <b>{cliente.codigo ?? cliente.id}</b>
                        {cliente.nombre ? ` — ${cliente.nombre}` : ""}
                      </>
                    ) : (
                      <span className="text-gray-500">Sin cliente</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* █ FIN agregado */}

          <div className="border rounded bg-gray-50">
            {/* Selector de Pines */}
            <div className="section-panel">
              <div className="form-row gap-2">
                <div className="form-label">Pin:</div>
                <div className="flex-1 relative">
                  <button
                    type="button"
                    onClick={() => setOpenPicker((o) => !o)}
                    className="w-full border rounded px-3 py-2 text-left bg-white hover:bg-gray-50"
                  >
                    {seleccionadas}{" "}
                    {seleccionadas === 1 ? "seleccionado" : "seleccionados"}
                  </button>

                  {openPicker && (
                    <div className="absolute z-10 mt-1 w-full border rounded bg-white shadow-lg">
                      <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2">
                        <input
                          placeholder="Filtrar registro"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="flex-1 border rounded px-2 py-1"
                        />
                        <span className="text-sm text-gray-600">Opciones:</span>
                        <button
                          type="button"
                          onClick={marcarTodas}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✓ Marcar todas
                        </button>
                        <button
                          type="button"
                          onClick={desmarcarTodas}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✗ Desmarcar todas
                        </button>
                      </div>

                      <div className="max-h-72 overflow-auto">
                        {filtrados.map((p) => (
                          <label
                            key={p.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(p.id)}
                              onChange={() => toggleOne(p.id)}
                            />
                            <span className="text-sm">{p.etiqueta}</span>
                          </label>
                        ))}
                        {filtrados.length === 0 && (
                          <div className="px-3 py-4 text-sm text-gray-500">
                            Sin resultados
                          </div>
                        )}
                      </div>

                      <div className="p-2 border-t text-right">
                        <button
                          type="button"
                          onClick={() => setOpenPicker(false)}
                          className="text-green-700 hover:text-green-800"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!canSave || saving}
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
            <button type="button" onClick={onClose} className="btn-fil">
              Consultar
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/* ---------- UI helpers ---------- */
function Modal({ title, children, onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-panel-md">
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">{title}</h2>
          {/* <button onClick={onClose} className="modal-close-x">✕</button> */}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
